import { SetStateAction, useEffect, useState } from "react";
import { Link, useLocation } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Pagination, PaginationItem, PaginationLink, Row } from 'reactstrap';
import { useFetchWrapper } from "../../../hooks/useFetchWrapper";
import { PermissionsGate } from '../../../utilities/PermissionsGate';
import { clientConfig } from "../../../utilities/configService";
import { ACCESS_VALUES, PageNames } from '../../../utilities/constValues';
import Helper from '../../../utilities/helper';
import { ContentBlock } from "../../Shared/Content/ContentBlock";
import { ContentBody } from "../../Shared/Content/ContentBody";
import { Hero } from "../../Shared/Hero";
import { Loading } from "../../Shared/Loading";
import { PermissionsRequired } from '../../Shared/PermissionsRequired';
import { FundingSourceCard } from "./FundingSourceCard";
import { NoResultsCard } from "./NoResultsCard";
import { MultiSelect } from 'primereact/multiselect';
import "primereact/resources/themes/lara-light-cyan/theme.css";
import 'primeicons/primeicons.css';

export interface IFundingSource {
    fundingSourceID: number
    fundingSourceName: string
    administratorName: fundingSourceAdministrators
    fundingSourceTypeName: string
    vehicleCategories: fundingSourceVehicleCategories
    eligibleCostsList: eligibleCostsList
    programOfferingDescription: string
    upcomingDeadlineDescription: string
    organizationTypes: organizationTypes
    sourceURL: string
}

export interface fundingSourceVehicleCategories {
    vehicleCategoryNames: string[]
}

export interface fundingSourceAdministrators {
    administratorNames: string[]
}

export interface organizationTypes {
    organizationTypeIDs: number[]
}

export interface eligibleCostsList {
    eligibleCosts: string[]
}

interface IFundingSourceFilter { 
    id: number,
    dropDownField: string,
    idName: string,
    value: string
}

interface IFundingSourceAdminFilter {
    fundingSourceAdministratorID: number,
    fundingSourceAdministratorName: string
}

type SortOrder = 'asc' | 'desc';

const orderByField = <T,>(
    items: T[],
    iteratee: keyof T | string | ((item: T) => unknown),
    order: SortOrder[] = ['asc']
): T[] => {
    const factor = order[0] === 'desc' ? -1 : 1;
    const getValue = typeof iteratee === 'function'
        ? iteratee
        : (item: T) => (item as any)[iteratee as string];

    return [...items].sort((a, b) => {
        const aValue = getValue(a) as any;
        const bValue = getValue(b) as any;

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return -1 * factor;
        if (bValue == null) return 1 * factor;

        if (aValue < bValue) return -1 * factor;
        if (aValue > bValue) return 1 * factor;
        return 0;
    });
};


export const FundingSources = () => {
    const fetchWrapper = useFetchWrapper();

    const applicationName = clientConfig?.APPLICATION_NAME || '';

    const [loadingResults, setLoadingResults] = useState(true);
    const [fundingSources, setFundingSources] = useState<IFundingSource[]>([]);
    const [allFundingSources, setAllFundingSources] = useState<IFundingSource[]>([]);

    const [filterEligibleCostOptions, setFilterEligibleCostOptions] = useState<IFundingSourceFilter[]>([]);
    const [filterFundingTypeOptions, setFilterFundingTypeOptions] = useState<IFundingSourceFilter[]>([]);
    const [filterOrganizationTypeOptions, setFilterOrganizationTypeOptions] = useState<IFundingSourceFilter[]>([]);
    const [filterVehicleCategoryOptions, setFilterVehicleCategoryOptions] = useState<IFundingSourceFilter[]>([]);
    const [filterAdministratorOptions, setFilterAdministratorOptions] = useState<IFundingSourceAdminFilter[]>([]); 

    const [filterProgramNameContains, setFilterProgramNameContains] = useState('');
    const [filterAdministratorSelection, setFilterAdministratorSelection] = useState<string[]>([]); 
    const [filterFundingTypeSelection, setFilterFundingTypeSelection] = useState<string[]>([]);
    const [filterOrganizationTypeSelection, setFilterOrganizationTypeSelection] = useState<string[]>([]);
    const [filterVehicleCategorySelection, setFilterVehicleCategorySelection] = useState<string[]>([]);
    const [filterEligleCostSelection, setFiltersEligibleCostSelection] = useState<string[]>([]);
    const [filterNotesContains, setFilterNotesContains] = useState('');

    const [currentSortValue, setCurrentSortValue] = useState('programName');
    const [currentSortDirection, setCurrentSortDirection] = useState<SortOrder[]>(["asc"]);
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 25;

    const location = useLocation();
    useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        getFundingSources();
        getFundingCatalogFilters();
        getAdministratorFilters();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getFundingSources = () => {
        setLoadingResults(true);
        var fetchUrl = `Funding/GetSourcesByApplication/${applicationName}`;

        fetchWrapper.get(fetchUrl)
            .then((data) => {
                if (data.success && data.fundingSources) {
                    setFundingSources(data.fundingSources);
                    setAllFundingSources(data.fundingSources);
                }
                setLoadingResults(false);
            });
    };

    //fetch funding sources filters
    const getFundingCatalogFilters = () => {
        fetchWrapper.get(`Funding/FundingSourceDropDowns`)
            .then((data) => {
                if (data.success && data.fundingSourceDropDowns) {
                    var _eligibleCost = filterDropDownsByField(data.fundingSourceDropDowns, 'Eligible Costs');
                    setFilterEligibleCostOptions(orderByField(_eligibleCost, item => item.value, ['asc']));
                    var _fundingType = filterDropDownsByField(data.fundingSourceDropDowns, 'Funding Type');
                    setFilterFundingTypeOptions(_fundingType);
                    var _organizationType = filterDropDownsByField(data.fundingSourceDropDowns, 'Organization Type');
                    setFilterOrganizationTypeOptions(_organizationType);
                    var _vehicleCategory = filterDropDownsByField(data.fundingSourceDropDowns, 'Vehicle Category');
                    setFilterVehicleCategoryOptions(orderByField(_vehicleCategory, item => item.value, ['asc']));
                }
            });
    };

    //fetch funding sources administrator filters
    const getAdministratorFilters = () => {
        var fetchUrl = `Funding/FundingSourceAdministratorDropDowns/${applicationName}`;
        fetchWrapper.get(fetchUrl)
            .then((data) => {
                if (data.success && data.fundingSourceAdministratorDropDowns) {
                    setFilterAdministratorOptions(orderByField(data.fundingSourceAdministratorDropDowns, (item: IFundingSourceAdminFilter) => item.fundingSourceAdministratorName, ['asc']));
                }
            });
    };

    const filterDropDownsByField = (array: IFundingSourceFilter[], field: string) => {
        return array.filter((item) => {
            return item.dropDownField === field;
        })
    }

    const getPageCount = (sourceLength: number) => {
        let totalCount = sourceLength;
        var pageCountCalc = Math.floor(totalCount / pageSize);
        if (totalCount % pageSize > 0) {
            pageCountCalc++;
        }
        return pageCountCalc;
    }

    const handleFirstClick = () => {
        setCurrentPage(0);
    }

    const handlePreviousClick = () => {
        setCurrentPage(currentPage - 1);
    }

    const handlePageClick = (index: SetStateAction<number>) => {
        setCurrentPage(index);
    };

    const handleNextClick = () => {
        setCurrentPage(currentPage + 1);
    };

    const handleLastClick = () => {
        setCurrentPage(getPageCount(fundingSources.length) - 1);
    }

    const setSorting = (selection: string) => {
        var sortDirection: SortOrder[] = ['asc'];
        if (selection === currentSortValue) {
            if (currentSortDirection[0] === 'asc') {
                sortDirection = ['desc'];
            }
            else {
                sortDirection = ['asc'];
            }
        }
        var sortedArray: IFundingSource[] = orderByField(fundingSources, selection, sortDirection);
        setFundingSources(sortedArray);
        setCurrentSortValue(selection);
        setCurrentSortDirection(sortDirection);
        setCurrentPage(0);
    }

    const filterResultsClick = (e: any) => {
        //apply selected filters to fundingSources
        let filteredArray: IFundingSource[] = allFundingSources;
        if (filterProgramNameContains && filterProgramNameContains !== '') {
            filteredArray = filteredArray.filter(o => o.fundingSourceName.toLowerCase().indexOf(filterProgramNameContains.toLowerCase()) >= 0);
        }
        if (filterNotesContains && filterNotesContains !== '') {
            filteredArray = filteredArray.filter(o => o.programOfferingDescription.toLowerCase().indexOf(filterNotesContains.toLowerCase()) >= 0
                || o.upcomingDeadlineDescription.toLowerCase().indexOf(filterNotesContains.toLowerCase()) >= 0);
        }
        if (filterEligleCostSelection && filterEligleCostSelection.length > 0) {
            filteredArray = filteredArray.filter(fsItem =>
                filterEligleCostSelection.find(filterItem => fsItem.eligibleCostsList.eligibleCosts.includes(filterItem))
            );
        }
        if (filterFundingTypeSelection && filterFundingTypeSelection.length > 0) {
            filteredArray = filteredArray.filter(fsItem =>
                filterFundingTypeSelection.find(filterItem => fsItem.fundingSourceTypeName.indexOf(filterItem) >= 0)
            );
        }
        if (filterOrganizationTypeSelection && filterOrganizationTypeSelection.length > 0) {
            filteredArray = filteredArray.filter(fsItem =>
                filterOrganizationTypeSelection.find(filterItem => fsItem.organizationTypes.organizationTypeIDs.includes(+filterItem))
            );
        }
        if (filterVehicleCategorySelection && filterVehicleCategorySelection.length > 0) {
            filteredArray = filteredArray.filter(fsItem =>
                filterVehicleCategorySelection.find(filterItem => fsItem.vehicleCategories.vehicleCategoryNames.includes(filterItem))
            );
        }
        if (filterAdministratorSelection && filterAdministratorSelection.length > 0) {
            //the Administrator dropdown's options have no "value" field, so it selects whole administrator objects
            const filterAdministratorNames = filterAdministratorSelection.map((item: any) => item.fundingSourceAdministratorName);

            filteredArray = filteredArray.filter(fsItem => 
                filterAdministratorNames.find(filterItem => fsItem.administratorName.administratorNames.includes(filterItem))
            );
        }


        setCurrentPage(0);
        console.log("currentSortDirection: ", currentSortDirection);
        setFundingSources(orderByField(filteredArray, currentSortValue, currentSortDirection));
    }

    const resetClick = (e: any) => {
        setFilterAdministratorSelection([]);
        setFilterFundingTypeSelection([]);
        setFilterNotesContains('');
        setFilterOrganizationTypeSelection([]);
        setFilterProgramNameContains('');
        setFiltersEligibleCostSelection([]);
        setFilterVehicleCategorySelection([]);
        setFundingSources(orderByField(allFundingSources, currentSortValue, currentSortDirection));
    }

    return (
        <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<PermissionsRequired showParticipantMsg={true} />} pageName={PageNames.FundingSources}>
            <div className="fundingSourcesContainer">
                <Hero pageTitle="Funding Sources">
                    These funding sources are available in your utility's service territory. Your fleet's eligibility for funding depends on a variety of factors. For more information, please <Link to="/ContactUs" target="_self">contact our program team</Link>.
                </Hero>
                <ContentBlock className="fundingSourcesFilterSection">
                    <ContentBody>
                        <Form>
                            <Row className="fundingSourcesFilterMainRow">
                                <Col lg={8} md={12}>
                                    <Row>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 1 }}>
                                            <FormGroup row >
                                                <Label for="programName" lg={12} md={12} sm={12} xs={4} className="filterLabel">
                                                    <Button className={`buttonAsLink small sorting active ${currentSortDirection}`} onClick={e => setSorting("fundingSourceName")}>Program Name</Button>
                                                </Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <InputGroup className="fieldWidth185">
                                                        <Input className="inlineInput inputWithButton" type="text" name="programName" key="programName" id="programName" placeholder="Search" value={filterProgramNameContains} onChange={e => setFilterProgramNameContains(e.target.value)} maxLength={255} />
                                                        <InputGroupText>
                                                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <g id="ICF-CL Icon Collection - search">
                                                                    <path id="icon_mag_glass" fill-rule="evenodd" clip-rule="evenodd" d="M17.2298 14.6914L13.4363 10.8888C13.0439 10.4967 12.4291 10.2027 11.932 10.255C13.6064 7.83094 13.3709 4.47909 11.2126 2.32293C8.78606 -0.107645 4.85525 -0.107645 2.42875 2.32293C0.00225091 4.75351 0.00879133 8.68687 2.42875 11.1175C4.59364 13.2867 7.96196 13.5154 10.3819 11.81C10.3296 12.2805 10.5912 12.8489 11.0294 13.2932L14.8229 17.0959C15.3134 17.5925 15.9675 17.6513 16.4645 17.1612L17.2625 16.3641C17.753 15.8675 17.7138 15.1946 17.2232 14.698L17.2298 14.6914ZM3.93959 9.63427C2.31757 8.01389 2.31757 5.38076 3.93959 3.76037C5.56162 2.13999 8.18434 2.13999 9.80637 3.76037C11.4284 5.38076 11.4284 8.01389 9.80637 9.63427C8.18434 11.2547 5.56162 11.2547 3.93959 9.63427Z" fill="#0785F2" />
                                                                </g>
                                                            </svg>
                                                        </InputGroupText>
                                                    </InputGroup>
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 2 }}>
                                            <FormGroup row >
                                                <Label for="fundingTypesMultiSelect" lg={12} md={12} sm={12} xs={4} className="filterLabel">Funding Types</Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <MultiSelect key="fundingTypes" name="fundingTypesMultiSelect" value={filterFundingTypeSelection} onChange={(e) => setFilterFundingTypeSelection(e.value)} options={filterFundingTypeOptions} optionLabel="value"
                                                        placeholder="Select..." maxSelectedLabels={3} className="fieldWidth185 proper-case prime-form-select" />
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 3 }}>
                                            <FormGroup row >
                                                <Label for="orgTypesMultiSelect" lg={12} md={12} sm={12} xs={4} className="filterLabel">Organization Types</Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <MultiSelect key="orgTypes" name="orgTypesMultiSelect" value={filterOrganizationTypeSelection} onChange={(e) => setFilterOrganizationTypeSelection(e.value)}
                                                        options={filterOrganizationTypeOptions} optionLabel="value" optionValue="id"
                                                        placeholder="Select..." maxSelectedLabels={3} className="fieldWidth185 proper-case prime-form-select" />
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 1 }}>
                                            <FormGroup row >
                                                <Label for="administratorMultiSelect" lg={12} md={12} sm={12} xs={4} className="filterLabel">Administrator</Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <MultiSelect key="administrator" name="administratorMultiSelect" value={filterAdministratorSelection} onChange={(e) => setFilterAdministratorSelection(e.value)} options={filterAdministratorOptions} optionLabel="fundingSourceAdministratorName"
                                                        placeholder="Select..." maxSelectedLabels={3} className="fieldWidth185 proper-case prime-form-select" />
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 2 }}>
                                            <FormGroup row >
                                                <Label for="vehCatsMultiSelect" lg={12} md={12} sm={12} xs={4} className="filterLabel">Vehicle Categories</Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <MultiSelect key="vehCats" name="vehCatsMultiSelect" value={filterVehicleCategorySelection} onChange={(e) => setFilterVehicleCategorySelection(e.value)} options={filterVehicleCategoryOptions} optionLabel="value"
                                                        placeholder="Select..." maxSelectedLabels={3} className="fieldWidth185 proper-case prime-form-select" />
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                        <Col lg={4} md={4} sm={{ size: 6, order: 1 }} xs={{ size: 12, order: 3 }}>
                                            <FormGroup row >
                                                <Label for="elCostsMultiSelect" lg={12} md={12} sm={12} xs={4} className="filterLabel">Eligible Costs</Label>
                                                <Col lg={12} md={12} sm={12} xs={8}>
                                                    <MultiSelect key="elCosts" name="elCostsMultiSelect" value={filterEligleCostSelection} onChange={(e) => setFiltersEligibleCostSelection(e.value)} options={filterEligibleCostOptions} optionLabel="value"
                                                        placeholder="Select..." maxSelectedLabels={3} className="fieldWidth185 proper-case prime-form-select" />
                                                </Col>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col lg={4} md={12} sm={12} xs={12} className="labelAlignment">
                                    <FormGroup row >
                                        <Label for="notes" lg={12} md={4} sm={12} xs={4} className="filterLabel">Notes</Label>
                                        <Col lg={12} md={8} sm={12} xs={8}>
                                            <InputGroup className="fieldWidth250">
                                                <Input className="inlineInput inputWithButton" type="text" name="notes" id="notes" placeholder="Search" value={filterNotesContains} onChange={e => setFilterNotesContains(e.target.value)} maxLength={255} />
                                                <InputGroupText>
                                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <g id="ICF-CL Icon Collection - search">
                                                            <path id="icon_mag_glass" fill-rule="evenodd" clip-rule="evenodd" d="M17.2298 14.6914L13.4363 10.8888C13.0439 10.4967 12.4291 10.2027 11.932 10.255C13.6064 7.83094 13.3709 4.47909 11.2126 2.32293C8.78606 -0.107645 4.85525 -0.107645 2.42875 2.32293C0.00225091 4.75351 0.00879133 8.68687 2.42875 11.1175C4.59364 13.2867 7.96196 13.5154 10.3819 11.81C10.3296 12.2805 10.5912 12.8489 11.0294 13.2932L14.8229 17.0959C15.3134 17.5925 15.9675 17.6513 16.4645 17.1612L17.2625 16.3641C17.753 15.8675 17.7138 15.1946 17.2232 14.698L17.2298 14.6914ZM3.93959 9.63427C2.31757 8.01389 2.31757 5.38076 3.93959 3.76037C5.56162 2.13999 8.18434 2.13999 9.80637 3.76037C11.4284 5.38076 11.4284 8.01389 9.80637 9.63427C8.18434 11.2547 5.56162 11.2547 3.93959 9.63427Z" fill="#0785F2" />
                                                        </g>
                                                    </svg>
                                                </InputGroupText>
                                            </InputGroup>
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </ContentBody>
                </ContentBlock>
                <ContentBlock className="fundingSourcesFilterButtonSection">
                    <ContentBody>
                        <Row className="submitButtonRow">
                            <Col className="submitButtonCol" lg={6} md={6} sm={6} xs={12}>
                                <Button className="submitButton hollowPos secondaryButton iconResetBlack" onClick={resetClick}>Reset Choices</Button>
                            </Col>
                            <Col className="submitButtonCol" lg={6} md={6} sm={6} xs={12}>
                                <Button type="button" className="solidNeg secondaryButton iconView btn btn-secondary" onClick={filterResultsClick}>Show Results</Button>
                            </Col>
                        </Row>
                    </ContentBody>
                </ContentBlock>
                {!loadingResults ? (
                    <ContentBlock className="fundingSourcesSection">
                        <ContentBody>
                            <div className="fundingSourcesPagination">
                                {fundingSources.length > 0 ? (
                                    <div className="fundingSourcesPaginationResultCount">{(currentPage * pageSize) + 1} - {fundingSources.length > (currentPage + 1) * pageSize ? (currentPage + 1) * pageSize : (fundingSources.length)} of {fundingSources.length} results</div>
                                ) : ('')}
                                {fundingSources.length > pageSize ? (
                                    <Pagination size="sm" className="paginator" >
                                        <PaginationItem>
                                            <PaginationLink first onClick={handleFirstClick} disabled={currentPage === 0} className="paginationIndicator" />
                                        </PaginationItem>
                                        <PaginationItem>
                                            <PaginationLink onClick={handlePreviousClick} previous disabled={currentPage === 0} className="paginationIndicator" />
                                        </PaginationItem>
                                        {[...Array(getPageCount(fundingSources.length))].map((tCount, i) => {
                                            return (<PaginationItem active={i === currentPage} key={i}>
                                                <PaginationLink onClick={e => handlePageClick(i)}>
                                                    {i + 1}
                                                </PaginationLink>
                                            </PaginationItem>
                                            )
                                        })}
                                        <PaginationItem>
                                            <PaginationLink onClick={handleNextClick} next disabled={currentPage === (getPageCount(fundingSources.length) - 1)} className="paginationIndicator" />
                                        </PaginationItem>
                                        <PaginationItem>
                                            <PaginationLink onClick={handleLastClick} last disabled={currentPage === (getPageCount(fundingSources.length) - 1)} className="paginationIndicator" />
                                        </PaginationItem>
                                    </Pagination>
                                ) : ('')}
                            </div>
                            {fundingSources.length > 0 ?
                                fundingSources.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map((item: IFundingSource, index: number) => {
                                    return (
                                        <Col sm={12} className="detailCol" key={index}>
                                            <FundingSourceCard fundingSourceItem={item} />
                                        </Col>
                                    );
                                }) : (<NoResultsCard />)}
                        </ContentBody>
                    </ContentBlock>
                ) : (
                    <Loading showContent={true} includeHero={false} />
                )}
            </div>
        </PermissionsGate>
    );
}